import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import {
  getInspections,
  Inspection,
  InspectionStatus,
} from '../../services/inspectionService';
import { getFlocs } from '../../services/flocService';
import {
  LocationFilterMap,
  filterItemsByLocation,
  formatDistance,
  LOCATION_RADIUS_OPTIONS,
  LocationData,
} from '../../components/LocationFilterMap';
import {
  PlusIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MapIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';

const STATUS_FILTERS: { value: InspectionStatus | 'all'; label: string; color: string }[] = [
  { value: 'all', label: 'All', color: 'gray' },
  { value: 'scheduled', label: 'Scheduled', color: 'blue' },
  { value: 'in_progress', label: 'In Progress', color: 'yellow' },
  { value: 'pending_review', label: 'Pending Review', color: 'purple' },
  { value: 'approved', label: 'Approved', color: 'green' },
  { value: 'rejected', label: 'Rejected', color: 'red' },
  { value: 'canceled', label: 'Canceled', color: 'gray' },
];

interface InspectionWithLocation extends Inspection {
  location?: LocationData;
  distance?: number;
}

export function InspectionsPageEnhanced() {
  const { currentOrg } = useOrg();
  const navigate = useNavigate();

  const [inspections, setInspections] = useState<InspectionWithLocation[]>([]);
  const [filteredInspections, setFilteredInspections] = useState<InspectionWithLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<InspectionStatus | 'all'>('all');
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  // Location state
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(null);
  const [locationRadius, setLocationRadius] = useState(LOCATION_RADIUS_OPTIONS[3].value); // Default 1km
  const [locationError, setLocationError] = useState('');
  const [locationWatchId, setLocationWatchId] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, [currentOrg]);

  useEffect(() => {
    filterInspections();
  }, [selectedStatus, inspections, locationEnabled, currentLocation, locationRadius]);

  // Auto-enable location when switching to map view
  useEffect(() => {
    if (viewMode === 'map' && !locationEnabled && !currentLocation) {
      enableLocation();
    }
  }, [viewMode]);

  // Cleanup location watch on unmount
  useEffect(() => {
    return () => {
      if (locationWatchId !== null) {
        navigator.geolocation.clearWatch(locationWatchId);
      }
    };
  }, [locationWatchId]);

  const loadData = async () => {
    if (!currentOrg) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [inspectionsData, flocsData] = await Promise.all([
        getInspections(currentOrg.id),
        getFlocs(currentOrg.id),
      ]);

      // Load assets for fallback lookup
      const { getAssets } = await import('../../services/assetService');
      const assetsData = await getAssets(currentOrg.id);

      // Create lookup maps
      const flocMap = new Map(flocsData.map(f => [f.id, f]));
      const assetMap = new Map(assetsData.map(a => [a.id, a]));

      // Enrich inspections with FLOC locations and missing details
      const enrichedInspections = inspectionsData.map(inspection => {
        const floc = flocMap.get(inspection.flocId || '');
        const asset = assetMap.get(inspection.assetId || '');

        return {
          ...inspection,
          // Fallback to loaded data if denormalized fields are missing
          flocName: inspection.flocName || floc?.name,
          flocCode: inspection.flocCode || floc?.code,
          assetName: inspection.assetName || asset?.name,
          assetTag: inspection.assetTag || asset?.assetId,
          location: floc?.location,
        };
      });

      setInspections(enrichedInspections);
      setFilteredInspections(enrichedInspections);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const enableLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setLocationError('');
    // Use watchPosition for continuous location updates
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationEnabled(true);
        setLocationError('');
      },
      (error) => {
        setLocationEnabled(false);
        setCurrentLocation(null);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location unavailable');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out');
            break;
          default:
            setLocationError('Unknown location error');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
    setLocationWatchId(watchId);
  };

  const disableLocation = () => {
    if (locationWatchId !== null) {
      navigator.geolocation.clearWatch(locationWatchId);
      setLocationWatchId(null);
    }
    setLocationEnabled(false);
    setCurrentLocation(null);
  };

  const filterInspections = () => {
    let filtered = inspections;

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(i => i.status === selectedStatus);
    }

    // Filter by location if enabled
    if (locationEnabled && currentLocation) {
      filtered = filterItemsByLocation(filtered, currentLocation, locationRadius);
    }

    setFilteredInspections(filtered);
  };

  const getStatusIcon = (status: InspectionStatus) => {
    switch (status) {
      case 'scheduled':
        return <ClockIcon className="w-5 h-5" />;
      case 'in_progress':
        return <ClipboardDocumentListIcon className="w-5 h-5" />;
      case 'completed':
      case 'submitted':
        return <CheckCircleIcon className="w-5 h-5" />;
      case 'pending_review':
        return <ClockIcon className="w-5 h-5" />;
      case 'approved':
      case 'reviewed':
        return <CheckCircleIcon className="w-5 h-5" />;
      case 'rejected':
      case 'failed':
        return <XCircleIcon className="w-5 h-5" />;
      case 'canceled':
        return <XCircleIcon className="w-5 h-5" />;
      default:
        return <ClipboardDocumentListIcon className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status: InspectionStatus) => {
    const filter = STATUS_FILTERS.find(f => f.value === status);
    return filter?.color || 'gray';
  };

  const renderMarkerPopup = (inspection: InspectionWithLocation, distance?: number) => (
    <div className="text-sm">
      <div className="font-semibold mb-1">{inspection.templateName}</div>
      <div className="text-xs text-gray-600 mb-1">
        Status: {inspection.status.replace('_', ' ')}
      </div>
      {inspection.flocName && (
        <div className="text-xs text-gray-600 mb-1">
          FLOC: {inspection.flocName}
          {inspection.flocCode && <span> ({inspection.flocCode})</span>}
        </div>
      )}
      {inspection.assetName && (
        <div className="text-xs text-gray-600 mb-1">
          Asset: {inspection.assetName}
          {inspection.assetTag && <span> ({inspection.assetTag})</span>}
        </div>
      )}
      {inspection.siteName && (
        <div className="text-xs text-gray-600 mb-1">
          Site: {inspection.siteName}
        </div>
      )}
      {distance && (
        <div className="text-xs text-blue-600 mb-2">
          Distance: {formatDistance(distance)}
        </div>
      )}
      <button
        onClick={() => navigate(`/inspections/${inspection.id}`)}
        className="text-xs text-blue-600 hover:underline"
      >
        View Details →
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading inspections...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inspections</h1>
          <p className="text-gray-600 mt-1">
            Conduct and manage asset inspections
          </p>
        </div>
        <div className="flex gap-2">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <ListBulletIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'map'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <MapIcon className="w-5 h-5" />
            </button>
          </div>

          <button
            onClick={() => navigate('/inspections/new')}
            className="btn btn-primary flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            New Inspection
          </button>
        </div>
      </div>

      {/* Status Filters */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {STATUS_FILTERS.map(filter => (
          <button
            key={filter.value}
            onClick={() => setSelectedStatus(filter.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              selectedStatus === filter.value
                ? `bg-${filter.color}-100 text-${filter.color}-800 ring-2 ring-${filter.color}-500`
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {filter.label}
            {filter.value === 'all' && (
              <span className="ml-2 text-xs">({filteredInspections.length})</span>
            )}
            {filter.value !== 'all' && (
              <span className="ml-2 text-xs">
                ({filteredInspections.filter(i => i.status === filter.value).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Map View */}
      {viewMode === 'map' && (
        <LocationFilterMap
          items={filteredInspections}
          locationEnabled={locationEnabled}
          currentLocation={currentLocation}
          locationRadius={locationRadius}
          locationError={locationError}
          onEnableLocation={enableLocation}
          onDisableLocation={disableLocation}
          onRadiusChange={setLocationRadius}
          renderMarkerPopup={renderMarkerPopup}
          onItemClick={(inspection) => navigate(`/inspections/${inspection.id}`)}
        />
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {filteredInspections.length === 0 ? (
            <div className="card text-center py-12">
              <ClipboardDocumentListIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {locationEnabled ? 'No nearby inspections' :
                 selectedStatus === 'all' ? 'No inspections yet' : `No ${selectedStatus} inspections`}
              </h3>
              <p className="text-gray-600 mb-4">
                {locationEnabled
                  ? 'Try increasing the radius or disabling location filtering'
                  : selectedStatus === 'all'
                  ? 'Get started by creating your first inspection'
                  : 'Try selecting a different status filter'}
              </p>
              {selectedStatus === 'all' && !locationEnabled && (
                <button
                  onClick={() => navigate('/inspections/new')}
                  className="btn btn-primary"
                >
                  New Inspection
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredInspections.map(inspection => (
                <div
                  key={inspection.id}
                  onClick={() => navigate(`/inspections/${inspection.id}`)}
                  className="card hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-${getStatusColor(
                            inspection.status
                          )}-100 text-${getStatusColor(inspection.status)}-800`}
                        >
                          {getStatusIcon(inspection.status)}
                          {inspection.status.replace('_', ' ')}
                        </span>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {inspection.templateName}
                        </h3>
                        {inspection.distance && (
                          <span className="text-sm text-blue-600 font-medium">
                            {formatDistance(inspection.distance)} away
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-2">
                        {inspection.flocName && (
                          <div>
                            <span className="font-medium">FLOC:</span> {inspection.flocName}
                            {inspection.flocCode && <span className="text-gray-500"> ({inspection.flocCode})</span>}
                          </div>
                        )}
                        {inspection.assetName && (
                          <div>
                            <span className="font-medium">Asset:</span> {inspection.assetName}
                            {inspection.assetTag && <span className="text-gray-500"> ({inspection.assetTag})</span>}
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Site:</span> {inspection.siteName}
                        </div>
                        <div>
                          <span className="font-medium">Inspector:</span> {inspection.inspectorName}
                        </div>
                        <div>
                          <span className="font-medium">Created:</span>{' '}
                          {inspection.createdAt &&
                            new Date(
                              (inspection.createdAt as any).seconds * 1000
                            ).toLocaleDateString()}
                        </div>
                      </div>

                      {inspection.scheduledDate && (
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Scheduled:</span>{' '}
                          {new Date(
                            (inspection.scheduledDate as any).seconds * 1000
                          ).toLocaleDateString()}
                        </div>
                      )}

                      {/* Reservation Status */}
                      {inspection.status === 'scheduled' && inspection.reservedBy && (
                        <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                          <ClockIcon className="w-4 h-4 text-amber-600" />
                          <span className="text-amber-900">
                            <span className="font-medium">Reserved by:</span> {inspection.reservedByName}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="text-right">
                      {inspection.completedAt && (
                        <div className="text-xs text-gray-500">
                          Completed{' '}
                          {new Date(
                            (inspection.completedAt as any).seconds * 1000
                          ).toLocaleDateString()}
                        </div>
                      )}
                      {inspection.reviewedAt && (
                        <div className="text-xs text-gray-500">
                          Reviewed by {inspection.reviewedBy}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
